import {SHOT_ANIMATION, SHOT_SHARDS_SHEET} from '../../configs/shotAnimation';

/**
 * Осколки и пыль от падения одного валуна катапульты. Чистая функция без
 * Phaser и DOM — по этому описанию рисует HexGridRenderer.showShotDebris.
 *
 * Камень бьет в землю и раскалывается: осколки разлетаются от точки удара В
 * СТОРОНЫ, вдоль земли (больше — дальше по ходу выстрела, камень несет
 * вперед). Подскок у осколка небольшой — доля его пути: он идет чуть по дуге,
 * а не вверх фонтаном. Крупные «глыбы» тяжелее — летят ближе, мелкая
 * «крошка» — дальше. Из-под удара выбивает клуб пыли.
 *
 * Осколок описан так же, как снаряд залпа (точки вылета и падения, высота
 * дуги): его положение считает общая resolveShotProjectilePosition.
 *
 * Состав каждый раз свой: два удара подряд выглядят по-разному. В состояние
 * партии случайность не попадает — эффект целиком визуальный.
 */

const randomRange = (rng, {min, max}) => min + rng() * (max - min);

/** Сколько осколков дает один валун залпа из boulderCount камней */
export const getShardCount = (boulderCount) => {
  const {totalBudget, perBoulder} = SHOT_ANIMATION.debris;
  const boulders = Math.max(1, Math.floor(Number(boulderCount)) || 1);
  return Math.max(perBoulder.min, Math.min(perBoulder.max, Math.floor(totalBudget / boulders)));
};

// Кадр спрайт-листа: глыбы лежат в начале листа, крошка — следом
const resolveShardFrame = (isBig, seed) => {
  const {frameCount, bigFrameCount} = SHOT_SHARDS_SHEET;
  const [first, amount] = isBig ? [0, bigFrameCount] : [bigFrameCount, frameCount - bigFrameCount];
  return first + Math.min(amount - 1, Math.floor(seed * amount));
};

/**
 * @param {Object} params
 * @param {{x: number, y: number}} params.at — точка удара валуна
 * @param {{x: number, y: number}} [params.from] — откуда валун летел: по этому
 *   направлению осколки несет вперед
 * @param {number} params.hexSize — размер ячейки: от него считаются все размеры
 * @param {number} [params.boulderCount] — сколько валунов в залпе
 * @param {Function} [params.rng] — источник случайности (в тестах — заглушка)
 * @returns {{shards: Object[], puffs: Object[]}}
 */
export const buildShotDebris = ({at, from, hexSize, boulderCount = 1, rng = Math.random} = {}) => {
  if (!at || !hexSize || hexSize <= 0) {
    return {shards: [], puffs: []};
  }

  const config = SHOT_ANIMATION.debris;
  const deltaX = from ? at.x - from.x : 0;
  const deltaY = from ? at.y - from.y : 0;
  const flightLength = Math.hypot(deltaX, deltaY);
  const forward = flightLength > 0 ? {x: deltaX / flightLength, y: deltaY / flightLength} : {x: 0, y: 0};

  const count = getShardCount(boulderCount);
  const bigCount = Math.max(1, Math.round(count * config.bigShare));
  // Осколки делят круг на сектора — веер без дыр и комков; сам веер каждый раз
  // повернут по-своему
  const sector = Math.PI * 2 / count;
  const turn = rng() * Math.PI * 2;

  const shards = [];
  for (let index = 0; index < count; index += 1) {
    const isBig = index < bigCount;
    const reach = isBig ? config.bigReachMultiplier : 1;
    const angle = turn + sector * (index + (rng() * 2 - 1) * config.angleJitterShare);
    const distance = hexSize * randomRange(rng, config.distanceFactor) * reach;
    const drift = hexSize * config.forwardDriftFactor * rng();
    shards.push({
      isBig,
      frame: resolveShardFrame(isBig, rng()),
      delay: Math.round(randomRange(rng, config.delayMs)),
      duration: Math.round(randomRange(rng, config.durationMs)),
      startX: at.x,
      startY: at.y,
      endX: at.x + Math.cos(angle) * distance + forward.x * drift,
      endY: at.y + Math.sin(angle) * distance * config.groundSquash + forward.y * drift,
      // Подскок — малая доля пути: осколок летит в сторону, а не вверх
      arcHeight: distance * randomRange(rng, config.arcShare),
      size: hexSize * randomRange(rng, isBig ? config.bigSizeFactor : config.smallSizeFactor),
      // Осколок кувыркается в свою сторону
      spinTurns: randomRange(rng, config.spinTurns) * (rng() < 0.5 ? -1 : 1),
      startAngleRad: rng() * Math.PI * 2,
    });
  }

  const puffConfig = config.puffs;
  const puffCount = boulderCount >= puffConfig.manyBouldersFrom ? puffConfig.count.many : puffConfig.count.few;
  const puffs = [];
  for (let index = 0; index < puffCount; index += 1) {
    const offsetAngle = rng() * Math.PI * 2;
    const offset = hexSize * puffConfig.offsetFactor * rng();
    puffs.push({
      x: at.x + Math.cos(offsetAngle) * offset,
      y: at.y + Math.sin(offsetAngle) * offset * config.groundSquash,
      textureSeed: rng(),
      startSize: hexSize * puffConfig.startSizeFactor,
      endSize: hexSize * randomRange(rng, puffConfig.endSizeFactor),
      alpha: puffConfig.alpha,
      duration: Math.round(randomRange(rng, puffConfig.durationMs)),
      angleDeg: rng() * 360,
      flipX: rng() < 0.5,
    });
  }

  return {shards, puffs};
};

/**
 * Как выглядит осколок на ходу своего полета: кувыркается, к концу мельчает
 * (уходит вдаль) и тает.
 *
 * @param {Object} shard — из buildShotDebris
 * @param {number} progress — ход полета от 0 до 1
 * @returns {{alpha: number, scaleMultiplier: number, rotation: number}}
 */
export const resolveShardAppearance = (shard, progress) => {
  const {fadeFromProgress, endScaleMultiplier} = SHOT_ANIMATION.debris;
  const t = Math.min(1, Math.max(0, progress));

  return {
    alpha: t <= fadeFromProgress ? 1 : 1 - (t - fadeFromProgress) / (1 - fadeFromProgress),
    scaleMultiplier: 1 + (endScaleMultiplier - 1) * t,
    rotation: shard.startAngleRad + shard.spinTurns * Math.PI * 2 * t,
  };
};
